import { useEffect, useCallback, useRef } from 'preact/hooks';
import { useForm } from 'react-hook-form';
import { useAIChatContextUpdater } from './useAIChat';
import { fieldUpdateHandler as fieldUpdateHandlerSignal } from '@/state/aiChat';
/**
 * Utility for handling AI list field updates.
 * Parses JSON array for full replacement, or appends a single item.
 */
export function handleListFieldUpdate(value, append) {
    try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
            for (const v of parsed) {
                append({ value: String(v) });
            }
            return;
        }
    }
    catch { /* not JSON — treat as single item */ }
    append({ value });
}
/**
 * Hook that combines react-hook-form with AI Chat integration.
 * Creates the form, auto-syncs AI context from field configs, and registers the field update handler.
 *
 * @example
 * const { form, getFormButtonState } = useAIForm<CampaignFormData>({
 *   mode: 'onChange',
 *   defaultValues: { Name: '', Description: '' }
 * }, {
 *   aiContext: {
 *     workspaceId: workspace.Id,
 *     location: ChatLocations.CampaignEditor,
 *     viewMode: ChatViewModes.Edit,
 *   },
 *   fields: [
 *     { aiName: 'campaignName', formPath: 'Name', displayName: 'Campaign Name', fieldType: 'text', maxLength: 200 },
 *     { aiName: 'campaignDescription', formPath: 'Description', displayName: 'Description', fieldType: 'textarea', maxLength: 5000 },
 *   ],
 *   isEditMode: true,
 * })
 */
export function useAIForm(formOptions, aiConfig) {
    const form = useForm(formOptions);
    const { setFieldUpdateHandler, updateContext } = useAIChatContextUpdater();
    const { setValue, trigger, formState, watch } = form;
    const { fields, onFieldUpdate, isEditMode = false, contextSuppressed, loadedData } = aiConfig;
    const { isValid, isDirty } = formState;
    // Use refs for callbacks to avoid effect re-runs on every render
    const onFieldUpdateRef = useRef(onFieldUpdate);
    onFieldUpdateRef.current = onFieldUpdate;
    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;
    // Watch all form values to trigger context re-sync
    const watchedValues = watch();
    // Auto-sync AI context whenever form values or config changes
    useEffect(() => {
        if (contextSuppressed)
            return;
        const currentFields = fieldsRef.current;
        const contextFields = currentFields.map(f => {
            let currentValue;
            if (f.isListField) {
                const listValue = form.getValues(f.formPath);
                const items = Array.isArray(listValue)
                    ? listValue.map((item) => (item?.value ?? '').trim()).filter(Boolean)
                    : [];
                currentValue = JSON.stringify(items);
            }
            else {
                currentValue = String(form.getValues(f.formPath) ?? '');
            }
            return {
                name: f.aiName,
                displayName: f.displayName,
                fieldType: f.fieldType,
                currentValue,
                isEditable: f.isEditable ?? true,
                isFocused: false,
                maxLength: f.maxLength,
                isListField: f.isListField,
            };
        });
        const fullContext = {
            ...aiConfig.aiContext,
            availableFields: contextFields,
            availableActions: ['ApplyToField', 'CopyToClipboard'],
            loadedData,
        };
        updateContext(fullContext);
    }, [watchedValues, contextSuppressed, fields, loadedData, updateContext, aiConfig.aiContext]);
    // Register field update handler
    useEffect(() => {
        if (contextSuppressed)
            return;
        const handler = async (fieldName, value) => {
            const currentFields = fieldsRef.current;
            // Match the aiName exactly, then fall back to matching after lowering
            // fieldName's first character. Webster's get_field_limits tool returns
            // PascalCase FieldKey identifiers (e.g. "DownloadTitle"), and the model
            // may reference those in its response when calling ApplyToField. The
            // bridge is intentionally temporary — it retires once aiName itself
            // migrates to PascalCase in a future unification ticket.
            const fieldConfig = currentFields.find(f => f.aiName === fieldName ||
                f.aiName === (fieldName[0]?.toLowerCase() ?? '') + fieldName.slice(1));
            if (!fieldConfig) {
                console.warn('[useAIForm] Unknown AI field name:', fieldName);
                return;
            }
            // List fields are backed by useFieldArray (an array of { value }). Calling
            // setValue on the array path with the raw string would corrupt it into a
            // string, breaking every consumer that iterates it. Route those entirely
            // through onFieldUpdate (which appends via the field array's append()).
            if (fieldConfig.isListField) {
                if (onFieldUpdateRef.current) {
                    await onFieldUpdateRef.current(fieldName, value);
                }
                return;
            }
            // Set the value on the form
            setValue(fieldConfig.formPath, value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
            await trigger(fieldConfig.formPath);
            // Call the consumer's onFieldUpdate for custom logic (e.g., list field routing)
            if (onFieldUpdateRef.current) {
                await onFieldUpdateRef.current(fieldName, value);
            }
        };
        setFieldUpdateHandler(handler);
        return () => {
            // Only unregister if we're still the current handler — prevents a parent's
            // cleanup from nulling out a child's handler during effect ordering.
            if (fieldUpdateHandlerSignal.value === handler) {
                setFieldUpdateHandler(null);
            }
        };
    }, [contextSuppressed, setValue, trigger, setFieldUpdateHandler]);
    /**
     * Calculate the standardized button state for forms
     */
    const getFormButtonState = useCallback((isSubmitting, hasExternalChanges) => {
        if (isSubmitting) {
            return {
                isSaveDisabled: true,
                saveText: 'Saving...',
                disabledReason: 'Form is submitting'
            };
        }
        if (!isValid) {
            return {
                isSaveDisabled: true,
                saveText: isEditMode ? 'Save Changes' : 'Save',
                disabledReason: 'Form validation failed'
            };
        }
        if (isEditMode && !isDirty && !hasExternalChanges) {
            return {
                isSaveDisabled: true,
                saveText: 'No Changes',
                disabledReason: 'No changes to save'
            };
        }
        return {
            isSaveDisabled: false,
            saveText: isEditMode ? 'Save Changes' : 'Save'
        };
    }, [isValid, isDirty, isEditMode]);
    return {
        form,
        getFormButtonState,
    };
}
